import { useGetUserConsent } from '@api/lendingAPI/lapiHooks';
import { datadogLogs } from '@datadog/browser-logs';
import { getCountry } from '@iwIntlProvider/CountryProvider';
import { tracking } from '@iwoca/frontend-tracking-library';
import { IconAngleLeft, IconShieldCheck } from '@iwoca/icons';
import { fetchPostUserConsent } from '@iwoca/lapi-client/edge';
import { Button, IwocaLogo, Spinner } from '@iwoca/orion';
import { Text } from '@iwoca/orion-ui';
import { useQuery } from '@tanstack/react-query';
import { FormattedMessage } from 'react-intl';

import { TOnlineBankWithProvider } from './BankSelectionModal.types';
import { getBankAuthUrl, getBankVerificationAuthUrl } from './getBankAuthUrl';
import { useStateKey } from '../../../iwQueryClient/useStateKey';
import FallbackErrorModal from '../fallbackErrorModal/FallbackErrorModal';

export const ConnectBank = ({
    selectedBank,
    unselectBank,
    linkingForBankVerification = false
}: {
    selectedBank: TOnlineBankWithProvider;
    unselectBank: () => void;
    linkingForBankVerification?: boolean;
}) => {
    const stateKey = useStateKey();
    const { userConsent } = useGetUserConsent();

    const queryFunction = linkingForBankVerification
        ? getBankVerificationAuthUrl
        : getBankAuthUrl;
    const { data, isLoading, isError } = useQuery({
        queryKey: [
            'getBankAuthUrl',
            linkingForBankVerification,
            selectedBank,
            stateKey
        ],
        queryFn: async () =>
            await queryFunction({
                bank: selectedBank,
                stateKey
            }),
        select: (data) => data.data
    });

    if (isLoading) {
        return (
            <div className="flex justify-center py-xl">
                <Spinner size="large" />
            </div>
        );
    }

    if (isError)
        return (
            <div>
                <Button onClick={unselectBank} variant="tertiary">
                    <IconAngleLeft />
                    <FormattedMessage
                        id="_.bank_selection_modal.back_button"
                        defaultMessage="Back"
                    />
                </Button>
                <FallbackErrorModal />
            </div>
        );

    const bankAuthUrl = data.provider_authorization_url;

    const trackBankConnectStarted = () => {
        tracking.ampli.trueLayerBankConnectStarted({
            bankId: selectedBank.bank_id,
            bankName: selectedBank.bank_name
        });
    };

    const handleBankAccountRedirect = async () => {
        trackBankConnectStarted();

        if (!userConsent) {
            try {
                if (!process.env.REACT_APP_BUILD_HASH)
                    throw new Error('Missing build hash');

                await fetchPostUserConsent({
                    stateKey,
                    provider: 'truelayer_open_banking',
                    body: {
                        data: {
                            bundle_hash: process.env.REACT_APP_BUILD_HASH
                        }
                    }
                });
            } catch (error) {
                datadogLogs.logger.error(
                    'User consent failed to save',
                    { REACT_APP_BUILD_HASH: process.env.REACT_APP_BUILD_HASH },
                    error as Error
                );
            }
        }

        window.location.href = bankAuthUrl;
    };

    if (getCountry() === 'DE') {
        trackBankConnectStarted();
        window.location.href = bankAuthUrl;

        return (
            <div>
                <Spinner />
                <Text>
                    <FormattedMessage
                        id="_.bank_selection_modal.redirecting"
                        defaultMessage="Redirecting you to our open banking provider..."
                    />
                </Text>
            </div>
        );
    }

    return (
        <div>
            <Button onClick={unselectBank} variant="tertiary">
                <IconAngleLeft />
                <FormattedMessage
                    id="_.bank_selection_modal.back_button"
                    defaultMessage="Back"
                />
            </Button>

            <div className="mx-auto flex flex-row items-center justify-center gap-m">
                <IwocaLogo iconOnly className="max-w-[40px]" />
                <TruelayerConnect />
                <img
                    src={selectedBank.logo_url}
                    alt={selectedBank.bank_name}
                    className="max-w-[50px]"
                />
            </div>

            <div className="mt-2xl space-y-l">
                <Text className="font-bold">
                    <FormattedMessage
                        id="_.bank_selection_modal.what_details_am_i_sharing"
                        defaultMessage="What details am I sharing?"
                    />
                </Text>

                <Text>
                    <FormattedMessage
                        id="_.bank_selection_modal.the_following_information_will_be_shared"
                        defaultMessage="The following information will be shared with iwoca LTD:"
                    />
                </Text>

                <ul className="list-inside list-disc">
                    <li>
                        <FormattedMessage
                            id="_.bank_selection_modal.full_name"
                            defaultMessage="Full name"
                        />
                    </li>
                    <li>
                        <FormattedMessage
                            id="_.bank_selection_modal.account_number_and_sort_code"
                            defaultMessage="Account number and sort code"
                        />
                    </li>
                    <li>
                        <FormattedMessage
                            id="_.bank_selection_modal.account_balance"
                            defaultMessage="Account balance"
                        />
                    </li>
                    <li>
                        <FormattedMessage
                            id="_.bank_selection_modal.transaction_history"
                            defaultMessage="Transaction history, direct debits and standing orders"
                        />
                    </li>
                </ul>

                <div className="space-y-s rounded-m bg-structure-95 p-m">
                    <div className="flex items-center gap-s">
                        <IconShieldCheck />
                        <Text className="text-xs font-bold">
                            <FormattedMessage
                                id="_.bank_selection_modal.bank_grade_encryption"
                                defaultMessage="Bank-grade encryption"
                            />
                        </Text>
                    </div>
                    <Text className="text-s">
                        <FormattedMessage
                            id="_.bank_selection_modal.we_use_a_partner_called_truelayer"
                            defaultMessage="We use a partner called Truelayer, to access the information needed to assess your ability to repay a loan."
                        />
                    </Text>

                    <Text className="text-s">
                        <FormattedMessage
                            id="_.bank_selection_modal.truelayer_permission"
                            defaultMessage="Truelayer needs your permission to access your information for 90 days."
                        />
                    </Text>

                    <Text className="text-s">
                        <FormattedMessage
                            id="_.bank_selection_modal.truelayer_regulation"
                            defaultMessage="Truelayer is FCA regulated, and won’t share or use your personal data for anything else."
                        />
                    </Text>
                </div>

                <div className="flex flex-col gap-l min-sm:flex-row">
                    <Button
                        variant="tertiary"
                        className="flex-1"
                        onClick={unselectBank}
                    >
                        <FormattedMessage
                            id="_.bank_selection_modal.cancel"
                            defaultMessage="Cancel"
                        />
                    </Button>
                    <Button
                        className="flex-1"
                        onClick={handleBankAccountRedirect}
                    >
                        <FormattedMessage
                            id="_.bank_selection_modal.allow"
                            defaultMessage="Allow"
                        />
                    </Button>
                </div>
            </div>
        </div>
    );
};

const TruelayerConnect = () => {
    return (
        <svg
            viewBox="0 0 110 34"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="max-w-[150px]"
        >
            <path
                d="m39.835 25.869 13.937 7.773c1.18.691 1.718.206 2.203-.007L70.76 25.33c.526-.327.888-.61.888-1.188s-.449-.87-.717-1.033l-3.98-2.602 3.784-2.15c.613-.3 1.266-.977 1.266-1.826v-5.724c0-1.489-.87-1.954-1.288-2.179L56.208.43c-1.256-.812-2.282-.276-2.706 0l-14.55 8.284c-.652.435-.86.901-.855 1.373.006.713.757 1.252.86 1.318l3.778 2.2L38.72 16c-.629.376-.673 1.075-.673 1.075-.046.313-.046 5.58-.046 5.58 0 1.383.129 2.284 1.835 3.214Zm29.187-1.613-14.166 7.766-13.693-7.557 4.295-2.413 8.888 4.976c.27.189.755.175 1.009.04l9.587-5.433 4.08 2.62Zm1.204-8.289L66.4 13.663l3.814-2.223.012 4.527ZM55.907 2.575l13.046 7.45-4.28 2.563L55.9 7.38l.008-4.805Zm-1.054 6.44 7.915 4.7-7.915 4.473h-.01l-8.092-4.577 8.092-4.597h.01Zm-14.337 1.067 13.306-7.524V7.38l-9.235 5.108-4.071-2.406Zm4.206 4.683 9.61 5.432a1.072 1.072 0 0 0 1.01.005l9.232-5.417 4.173 2.479-13.904 7.777-14.154-7.984 4.033-2.292Zm-4.846 4.102L43.6 20.95l-3.723 2.116v-4.198Z"
                fill="#000"
            />
            <circle cx="79.5" cy="17" r="2" fill="#D9D9D9" />
            <circle cx="2" cy="18" r="2" fill="#D9D9D9" />
            <circle cx="93.5" cy="17" r="2" fill="#D9D9D9" />
            <circle cx="16" cy="18" r="2" fill="#D9D9D9" />
            <circle cx="107.5" cy="17" r="2" fill="#D9D9D9" />
            <circle cx="30" cy="18" r="2" fill="#D9D9D9" />
        </svg>
    );
};
